import {
  alpha,
  Box,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import type { BarProps } from '@mui/x-charts/BarChart';
import { ChartsLabelMark } from '@mui/x-charts/ChartsLabel';
import type { ChartsLegendProps } from '@mui/x-charts/ChartsLegend';
import { useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { useLegend } from '@mui/x-charts/hooks';
import { CardSection, CardSubSection, useIsMobile } from '@side/pantry';
import { useMemo, useRef } from 'react';
import { formatPrice } from '@/common/utils/money';
import { ChartTooltip, ChartTooltipItem } from '../ChartTooltip/ChartTooltip';
import { useCompensationReportFilters } from '../CompensationReportFilters/CompensationReportFilters.helpers';
import { ExportToPNGButton } from '../components/ExportToPNGButton';
import { GlossaryTooltip } from '../components/GlossaryTooltip';
import { FilterValuesFooter } from '../FilterValuesFooter/FilterValuesFooter';
import { formatCompactPrice } from '../helpers';
import { NoResultsOverlay } from '../NoResultsOverlay/NoResultsOverlay';
import { useReportsRole } from '../ReportsConfiguration/useReportsRole';
import { MONTHS } from '../types';
import {
  ADJUSTED_GCI_CONFIG,
  getMonthlyEarningsFieldLabelByRole,
  getNiceYAxisTicks,
  IMAGE_NAME,
  isFutureMonth,
  type MonthlyEarningsFields,
  useMonthlyEarnings,
  useSeriesConfig,
} from './MonthlyEarningsTimeline.helpers';

interface BarWithExteriorStrokeProps extends BarProps {
  topSeriesId: string | undefined;
}

function BarWithExteriorStroke({
  topSeriesId,
  ...props
}: BarWithExteriorStrokeProps) {
  const theme = useTheme();
  const strokeColor = theme.palette.other.spaceCadet;
  const {
    seriesId,
    x,
    y,
    width,
    height,
    color,
    dataIndex,
    xOrigin,
    yOrigin,
    skipAnimation,
    ownerState,
    ...rest
  } = props;

  // Prevent rendering stroke "stubs" for empty data points/zero values.
  if (height <= 0) return null;

  const isEmptyState = seriesId === 'empty_state';
  const isBottom = seriesId === 'agentGrossShare';
  const isTop = seriesId === topSeriesId;
  const i = 0.5;

  if (isEmptyState) {
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        stroke="none"
      />
    );
  }

  return (
    <g>
      <rect
        {...rest}
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        stroke="none"
      />
      {/* Left edge: every bar draws its segment so the full side is visible */}
      <line
        x1={x + i}
        y1={y + height}
        x2={x + i}
        y2={y}
        stroke={strokeColor}
        strokeWidth={1}
      />
      {/* Right edge: every bar draws its segment */}
      <line
        x1={x + width - i}
        y1={y + height}
        x2={x + width - i}
        y2={y}
        stroke={strokeColor}
        strokeWidth={1}
      />
      {isBottom && (
        <line
          x1={x + i}
          y1={y + height - i}
          x2={x + width - i}
          y2={y + height - i}
          stroke={strokeColor}
          strokeWidth={1}
        />
      )}
      {isTop && (
        <line
          x1={x + i}
          y1={y + i}
          x2={x + width - i}
          y2={y + i}
          stroke={strokeColor}
          strokeWidth={1}
        />
      )}
    </g>
  );
}

function MonthlyEarningsLegend(props: ChartsLegendProps) {
  const { direction = 'horizontal', sx, className } = props;
  const { items } = useLegend();
  const theme = useTheme();

  const visibleItems = items.filter(
    (item): item is typeof item & { seriesId: string } =>
      'seriesId' in item && item.seriesId !== 'empty_state',
  );

  if (visibleItems.length === 0) return null;

  return (
    <Box
      component="ul"
      className={className}
      sx={{
        ...theme.typography.caption,
        lineHeight: '100%',
        display: 'flex',
        flexDirection: direction === 'vertical' ? 'column' : 'row',
        alignItems: direction === 'vertical' ? undefined : 'center',
        flexShrink: 0,
        gap: 2,
        listStyleType: 'none',
        paddingInlineStart: 0,
        marginBlock: 1,
        flexWrap: 'wrap',
        gridArea: 'legend',
        '& li': {
          display: direction === 'horizontal' ? 'inline-flex' : undefined,
        },
        ...sx,
      }}
    >
      {visibleItems.map((item, i) => (
        <Box
          component="li"
          key={`${item.seriesId}-${item.dataIndex ?? i}`}
          data-series={item.seriesId}
          data-index={item.dataIndex ?? undefined}
        >
          <Box
            component="span"
            sx={{
              display: direction === 'vertical' ? 'flex' : 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <ChartsLabelMark color={item.color} type={item.markType} />
            <GlossaryTooltip
              label={item.label}
              darkColor={item.color}
              lightColor={alpha(item.color, 0.3)}
            >
              <Typography variant="body2" component="span">
                {item.label}
              </Typography>
            </GlossaryTooltip>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function MonthlyEarningsTooltip() {
  const tooltipData = useAxesTooltip();
  const { monthlyEarnings } = useMonthlyEarnings();
  const seriesConfig = useSeriesConfig();
  const { role } = useReportsRole();

  const axisData = tooltipData?.[0];
  const { axisValue: monthValue } = axisData ?? {};

  const rowForMonth =
    typeof monthValue === 'string'
      ? monthlyEarnings.find((r) => r.monthLabel === monthValue)
      : monthlyEarnings[Number(monthValue)];
  const hasValues =
    rowForMonth?.weightedAdjustedGrossCommission != null &&
    rowForMonth.weightedAdjustedGrossCommission > 0;

  if (!axisData || !hasValues) return <ChartTooltip>{null}</ChartTooltip>;

  const adjustedGciValue = formatPrice(
    rowForMonth.weightedAdjustedGrossCommission,
    true,
    false,
  );

  return (
    <ChartTooltip>
      {seriesConfig.map((config) => {
        const value =
          (rowForMonth as MonthlyEarningsFields)?.[config.dataKey] ?? 0;
        return (
          <ChartTooltipItem
            key={config.dataKey}
            value={formatPrice(value, true, false)}
            label={config.label}
            color={config.color ?? 'grey.400'}
            markType="square"
          />
        );
      })}
      <Divider />
      <ChartTooltipItem
        value={adjustedGciValue}
        label={
          getMonthlyEarningsFieldLabelByRole(
            ADJUSTED_GCI_CONFIG.dataKey,
            role,
          ) ?? ADJUSTED_GCI_CONFIG.label
        }
        color="white"
        markType="square"
      />
    </ChartTooltip>
  );
}

export function MonthlyEarningsTimeline() {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const fontFamily = theme.typography.fontFamily;
  const { filters } = useCompensationReportFilters();
  const { monthlyEarnings, isLoading, noResults } = useMonthlyEarnings();
  const seriesConfig = useSeriesConfig();
  const { isAgent, isTeamLead } = useReportsRole();
  const isMobile = useIsMobile('md');

  const showGciGap = isAgent || isTeamLead;

  const yAxisMax = Math.max(
    ...monthlyEarnings.map((row) => row.weightedAdjustedGrossCommission ?? 0),
    0,
  );
  const yAxisTicks = getNiceYAxisTicks(yAxisMax);
  const yAxisDomainMax =
    yAxisTicks.length > 0 ? Math.max(...yAxisTicks) : 100_000;

  const dataset = monthlyEarnings.map((row) => {
    const showGrayBar =
      noResults ||
      isLoading ||
      (typeof filters.reportingPeriod === 'number' &&
        isFutureMonth({
          selectedYear: filters.reportingPeriod,
          monthIndex: row.periodMonth ?? 0,
        }));

    if (showGrayBar) {
      return {
        ...row,
        empty_state: showGrayBar ? yAxisDomainMax : 0,
      };
    }

    const totalGci = row.weightedAdjustedGrossCommission ?? 0;
    const segmentsSum = seriesConfig.reduce(
      (sum, config) =>
        sum + Number((row as MonthlyEarningsFields)[config.dataKey] ?? 0),
      0,
    );
    const gciGap = showGciGap ? Math.max(0, totalGci - segmentsSum) : 0;

    return {
      ...row,
      ...(showGciGap ? { gciGap } : {}),
    };
  });

  const topSeriesId = showGciGap ? 'gciGap' : seriesConfig.at(-1)?.dataKey;

  const barSlot = useMemo(() => {
    return (props: BarProps) => (
      <BarWithExteriorStroke {...props} topSeriesId={topSeriesId} />
    );
  }, [topSeriesId]);

  return (
    <CardSection
      id="monthly-earnings-timeline-card-section"
      cardTitle="Monthly Earnings Timeline"
      action={
        <ExportToPNGButton
          disabled={noResults || isLoading}
          chartElement={chartRef.current}
          imageName={IMAGE_NAME}
        />
      }
    >
      <CardSubSection>
        <Stack sx={{ gap: 2 }} ref={chartRef}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Shows earned commission by month. Future months appear as earnings
            are recorded.
          </Typography>
          <NoResultsOverlay
            isActive={noResults}
            message="No earnings match the current filters"
          >
            <BarChart
              dataset={dataset}
              height={300}
              margin={{
                top: 55,
                left: isMobile ? 0 : 20,
                right: isMobile ? 0 : 20,
              }}
              axisHighlight={{ x: 'none' }}
              series={[
                {
                  id: 'empty_state',
                  dataKey: 'empty_state',
                  stack: 'earnings',
                  label: '',
                  color: theme.palette.grey[200],
                  valueFormatter: () => '',
                },
                ...seriesConfig.map(({ dataKey, label, color }) => ({
                  id: dataKey,
                  dataKey,
                  stack: 'earnings',
                  label,
                  color: color ?? undefined,
                  valueFormatter: (v: number | null) =>
                    v == null ? '' : formatPrice(v, true, false),
                })),
                ...(showGciGap
                  ? [
                      {
                        id: 'gciGap',
                        dataKey: 'gciGap',
                        stack: 'earnings',
                        label: '',
                        color: theme.palette.common.white,
                        valueFormatter: () => '',
                      },
                    ]
                  : []),
              ]}
              xAxis={[
                {
                  dataKey: 'monthLabel',
                  tickPlacement: 'end',
                  valueFormatter: (value: string | number) => {
                    const str =
                      typeof value === 'number' ? MONTHS[value] : value;
                    if (isMobile && typeof str === 'string' && str.length > 6) {
                      return str.slice(0, 3);
                    }
                    return str;
                  },
                  tickLabelStyle: {
                    fontSize: 11,
                    fontFamily,
                  },
                },
              ]}
              yAxis={[
                {
                  dataKey: 'weightedAdjustedGrossCommission',
                  width: 64,
                  tickInterval: yAxisTicks,
                  valueFormatter: (value: unknown) =>
                    formatCompactPrice(Number(value)),
                  tickLabelStyle: {
                    overflow: 'visible',
                    textOverflow: 'clip',
                    whiteSpace: 'nowrap',
                    fontSize: 11,
                    fontFamily,
                  },
                  domainLimit: () => ({ min: 0, max: yAxisDomainMax }),
                },
              ]}
              slotProps={{
                tooltip: { trigger: 'axis' },
                legend: {
                  position: { horizontal: 'start' },
                  sx: {
                    color: theme.palette.text.secondary,
                    fontWeight: theme.typography.fontWeightRegular,
                    '& [data-series="empty_state"]': {
                      display: 'none',
                    },
                    '& [data-series="gciGap"]': {
                      display: 'none',
                    },
                  },
                },
              }}
              slots={{
                tooltip: MonthlyEarningsTooltip,
                bar: barSlot,
                legend: isLoading
                  ? () => (
                      <Skeleton variant="rectangular" width={192} height={20} />
                    )
                  : MonthlyEarningsLegend,
              }}
            />
          </NoResultsOverlay>
          <FilterValuesFooter variant="compensation" sx={{ pt: 1 }} />
        </Stack>
      </CardSubSection>
    </CardSection>
  );
}
